import { PERMISSION_MODULES } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can } from '../../app/guards';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

interface Role {
  id: string;
  code: string;
  nameAr: string;
  nameEn: string;
  isSystem: boolean;
  permissions: string[];
  _count: { users: number };
}

export function RolesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<Role[]>('/roles') });
  const catalog = useQuery({ queryKey: ['permissions-catalog'], queryFn: () => api<string[]>('/roles/permissions-catalog') });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());

  const selected = roles.data?.find((r) => r.id === selectedId) ?? roles.data?.[0] ?? null;
  useEffect(() => {
    if (selected) setDraft(new Set(selected.permissions));
  }, [selected]);

  const byModule = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of catalog.data ?? []) {
      const [m] = p.split(':');
      map.set(m!, [...(map.get(m!) ?? []), p]);
    }
    return map;
  }, [catalog.data]);

  const locked = !selected || (selected.isSystem && (selected.code === 'SYSTEM_ADMIN' || selected.code === 'DRIVER')) || !can('roles:update');
  const dirty = selected ? draft.size !== selected.permissions.length || selected.permissions.some((p) => !draft.has(p)) : false;

  const save = useMutation({
    mutationFn: () => api(`/roles/${selected!.id}`, { method: 'PATCH', json: { permissions: [...draft] } }),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['roles'] }); },
    onError: toast.error,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success(t('common.saved')); setSelectedId(null); void qc.invalidateQueries({ queryKey: ['roles'] }); },
    onError: toast.error,
  });

  const toggle = (p: string) => setDraft((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });
  const toggleModule = (perms: string[]) => setDraft((s) => { const n = new Set(s); const all = perms.every((p) => n.has(p)); perms.forEach((p) => (all ? n.delete(p) : n.add(p))); return n; });

  if (roles.isLoading || catalog.isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('settings.roles.title')} subtitle={t('settings.roles.subtitle')} actions={<Can perm="roles:create"><button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} aria-hidden /> {t('settings.roles.add')}</button></Can>} />
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <ul className="card divide-y divide-border-soft overflow-hidden">
          {roles.data?.map((r) => (
            <li key={r.id}>
              <button className={`flex w-full items-center gap-2 px-4 py-3 text-start text-sm ${selected?.id === r.id ? 'bg-brand-soft text-brand-ink' : 'hover:bg-surface-hover'}`} onClick={() => setSelectedId(r.id)}>
                <span className="flex-1 font-medium">{i18n.language === 'en' ? r.nameEn : r.nameAr}</span>
                {r.isSystem ? <Lock size={12} className="text-text-faint" aria-label={t('settings.roles.system')} /> : null}
                <Badge tone="neutral">{r._count.users}</Badge>
              </button>
            </li>
          ))}
        </ul>

        {selected ? (
          <div className="card flex flex-col">
            <div className="flex flex-wrap items-center gap-3 border-b border-border-soft px-5 py-4">
              <div className="flex-1">
                <h3 className="font-bold">{i18n.language === 'en' ? selected.nameEn : selected.nameAr} <span className="text-xs font-normal text-text-faint" dir="ltr">{selected.code}</span></h3>
                <p className="text-xs text-text-muted">{t('settings.roles.count', { count: draft.size })}</p>
              </div>
              {!selected.isSystem && can('roles:delete') ? <button className="btn-ghost text-danger-fg" onClick={() => confirmDialog(t('settings.roles.deleteConfirm'), { danger: true }).then((ok) => ok && remove.mutate(selected.id))}><Trash2 size={16} /> {t('common.delete')}</button> : null}
              {!locked ? <button className="btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button> : null}
            </div>
            {locked && selected.isSystem ? <p className="mx-5 mt-4 rounded-md bg-neutral-bg px-3 py-2 text-xs text-neutral-fg">{t('settings.roles.lockedHint')}</p> : null}
            <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
              {PERMISSION_MODULES.map((m) => {
                const perms = byModule.get(m) ?? [];
                const all = perms.every((p) => draft.has(p));
                return (
                  <fieldset key={m} disabled={locked} className="rounded-lg border border-border-soft p-3">
                    <legend className="px-1">
                      <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" className="accent-brand" checked={all} onChange={() => toggleModule(perms)} />
                        {t(`modules.${m}`)}
                      </label>
                    </legend>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {perms.map((p) => {
                        const action = p.split(':')[1]!;
                        return (
                          <label key={p} className="flex min-h-8 cursor-pointer items-center gap-1.5 rounded-md border border-border px-2 text-xs has-[:checked]:border-brand-ink has-[:checked]:bg-brand-soft has-[:checked]:text-brand-ink">
                            <input type="checkbox" className="accent-brand" checked={draft.has(p)} onChange={() => toggle(p)} />
                            {t(`actions.${action}`)}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {creating ? <CreateRoleDialog onClose={() => setCreating(false)} onCreated={(id) => setSelectedId(id)} /> : null}
    </div>
  );
}

function CreateRoleDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [v, setV] = useState({ code: '', nameAr: '', nameEn: '' });
  const save = useMutation({
    mutationFn: () => api<Role>('/roles', { method: 'POST', json: { ...v, code: v.code.toUpperCase().replace(/[^A-Z0-9_]/g, '_'), permissions: [] } }),
    onSuccess: (r) => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['roles'] }); onCreated(r.id); onClose(); },
    onError: toast.error,
  });
  return (
    <Dialog open onClose={onClose} title={t('settings.roles.add')} footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" disabled={!v.code || !v.nameAr || !v.nameEn || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <div className="grid gap-4">
        <Field label={t('settings.roles.code')} required hint="UPPER_SNAKE_CASE">{(id) => <input id={id} dir="ltr" className="input" value={v.code} onChange={(e) => setV({ ...v, code: e.target.value })} />}</Field>
        <Field label={t('settings.roles.nameAr')} required>{(id) => <input id={id} className="input" value={v.nameAr} onChange={(e) => setV({ ...v, nameAr: e.target.value })} />}</Field>
        <Field label={t('settings.roles.nameEn')} required>{(id) => <input id={id} dir="ltr" className="input" value={v.nameEn} onChange={(e) => setV({ ...v, nameEn: e.target.value })} />}</Field>
      </div>
    </Dialog>
  );
}
